import React, { createContext, useCallback, useMemo, useState } from 'react';
import { Data } from '../data/Data';
import { useLotsCommerciaux } from './lotCommercialContext';
import { useNotificationsManager } from './notificationContext';

const DocumentContext = createContext();
const DocumentManagerContext = createContext();

export const DocumentContextProvider = ({ children }) => {

    const { idOperationActive, idLotCommercialActif, lotsCommerciaux } = useLotsCommerciaux();
    const { dismiss, extractErrorMessages, inform } = useNotificationsManager();

    const [allDocuments, setAllDocuments] = useState([]);
    const [loaded, setLoaded] = useState([]);

    const loadDocuments = useCallback((idOperation, idLotCommercial) => {
        const id = inform("Chargement des documents...");

        Data._getDocuments(idOperation, idLotCommercial).then(response => {
            if (response.codeRetour === 0) {
                const objet = response.reponseAPIobjet;
                let { plans, contratEtActe, documentsAdministratifs, courriers, personnalisationDuLogement, piecesJointes, documentsCommerciaux, lePlusByEiffage, monSyndic, mesOffres } = objet || {};

                // Mise à jour des documents : merge avec ceux déjà téléchargés + ajout des tags idOperation et idLotCommercial
                setAllDocuments(prev => [
                    ...prev,
                    ...plans.map(document => ({ ...document, idOperation, idLotCommercial, type: "plans" })),
                    ...contratEtActe.map(document => ({ ...document, idOperation, idLotCommercial, type: "contratEtActe" })),
                    ...documentsAdministratifs.map(document => ({ ...document, idOperation, idLotCommercial, type: "documentsAdministratifs" })),
                    ...courriers.map(document => ({ ...document, idOperation, idLotCommercial, type: "courriers" })),
                    ...personnalisationDuLogement.map(document => ({ ...document, idOperation, idLotCommercial, type: "personnalisationDuLogement" })),
                    ...piecesJointes.map(document => ({ ...document, idOperation, idLotCommercial, type: "piecesJointes" })),
                    ...documentsCommerciaux.map(document => ({ ...document, idOperation, idLotCommercial, type: "documentsCommerciaux" })),
                    ...lePlusByEiffage.map(document => ({ ...document, idOperation, idLotCommercial, type: "lePlusByEiffage" })),
                    ...monSyndic.map(document => ({ ...document, idOperation, idLotCommercial, type: "monSyndic" })),
                    ...mesOffres.map(document => ({ ...document, idOperation, idLotCommercial, type: "mesOffres" })),
                ]);

                setLoaded(prev => [...prev, { idOperation, idLotCommercial }]);
            }
            else extractErrorMessages(response);
            dismiss(id);
        });
    }, [dismiss, extractErrorMessages, inform]);

    const markAsRead = useCallback((document) => {
        // Silencieux ; pas de notification
        if (!!document) {
            const { rowGUID } = document;
            // Récupération des infos du documents (si téléchargement depuis la messagerie alors on ne sais pas si le document a été lu ou non)
            if (!document.lu) {
                const localDocument = allDocuments.find(document => document.rowGUID === rowGUID);
                if (!!localDocument) document.lu = localDocument.lu;
            }
            if (!document.lu) {
                Data._markDocumentAsRead(rowGUID).then(response => {
                    if (response.codeRetour === 0) {
                        setAllDocuments(prev => {
                            const index = prev.findIndex(document => document.rowGUID === rowGUID);
                            const document = { ...prev[index], lu: true };
                            let documents = [...prev];
                            documents.splice(index, 1, document);
                            return documents;
                        });
                    }
                });
            }
        }
    }, [allDocuments]);

    const loadFileData = useCallback(async (rowGUID) => {
        const id = inform("Téléchargement des données en cours...");

        let fileData;
        const response = await Data._getFileData(rowGUID);
        if (response.codeRetour === 0) {
            const objet = response.reponseAPIobjet;
            fileData = objet.fileData;
            setAllDocuments(prev => {
                // Mise à jour du filedata du document
                const index = prev.findIndex(document => document.rowGUID === rowGUID);
                if (index >= 0) {
                    const newDocument = { ...prev[index], fileData };
                    let newDocuments = [...prev];
                    newDocuments[index] = newDocument;
                    return newDocuments;
                }
            });
        }
        else extractErrorMessages(response);

        dismiss(id);

        return fileData;
    }, [dismiss, extractErrorMessages, inform]);

    const addAttachment = useCallback((attachment, lu) => {
        const { rowGUID, fileData, idOperation, idReservation, nom } = attachment;
        const lotCommercial = lotsCommerciaux.find(lot => lot.idOperation === idOperation && lot.idReservation === idReservation);
        const document = {
            categorie: "Contrat et annexes",
            creerLe: Date.now(),
            fileData,
            idLotCommercial: lotCommercial?.idLotCommercial || idLotCommercialActif,
            idOperation: idOperation || idOperationActive,
            lu,
            nom,
            rowGUID,
            type: "piecesJointes"
        };
        setAllDocuments(prev => [...prev, document]);
    }, [idOperationActive, idLotCommercialActif, lotsCommerciaux]);

    const getFileData = useCallback(async (document) => {
        const rowGUID = document.rowGUID;
        let fileData = document.fileData;
        // Récupération des infos du documents (si téléchargement depuis la messagerie alors on ne sais pas si les fileData ont déjà été téléchargées ou non)
        if (!fileData) {
            const localDocument = allDocuments.find(document => document.rowGUID === rowGUID);
            if (!!localDocument) fileData = localDocument.fileData;
        }
        if (!fileData)
            fileData = await loadFileData(rowGUID);
        return fileData;
    }, [allDocuments, loadFileData]);

    const documents = useMemo(() => {
        if (!idOperationActive || !idLotCommercialActif) return [];
        const alreadyLoaded = loaded.some(item => item.idOperation === idOperationActive && item.idLotCommercial === idLotCommercialActif);
        if (!alreadyLoaded) {
            loadDocuments(idOperationActive, idLotCommercialActif);
            return [];
        }
        else return allDocuments.filter(document => document.idOperation === idOperationActive && document.idLotCommercial === idLotCommercialActif);
    }, [allDocuments, idOperationActive, idLotCommercialActif, loadDocuments, loaded]);

    const unreadBenefits = useMemo(() => {
        if (!Array.isArray(documents)) return 0;
        else return documents.filter(document => ["lePlusByEiffage", "monSyndic", "mesOffres"].includes(document.type) && !document.lu).length;
    }, [documents]);

    const unreadDocuments = useMemo(() => {
        if (!Array.isArray(documents)) return 0;
        else return documents.filter(document => !["lePlusByEiffage", "monSyndic", "mesOffres"].includes(document.type) && !document.lu).length;
    }, [documents]);

    const resetDocuments = useCallback(() => {
        setAllDocuments([]);
        setLoaded([]);
    }, []);

    const value = useMemo(
        () => ({ documents, unreadBenefits, unreadDocuments }),
        [documents, unreadBenefits, unreadDocuments]
    );

    const managerValue = useMemo(
        () => ({ addAttachment, getFileData, markAsRead, resetDocuments }),
        [addAttachment, getFileData, markAsRead, resetDocuments]
    );

    return <DocumentManagerContext.Provider value={managerValue}>
        <DocumentContext.Provider value={value}>
            {children}
        </DocumentContext.Provider>
    </DocumentManagerContext.Provider>;

};

export const useDocuments = () => {
    const data = React.useContext(DocumentContext);
    if (data === undefined) throw new Error();
    const { documents, unreadBenefits, unreadDocuments } = data;
    return { documents, unreadBenefits, unreadDocuments };
};

export const useDocumentsManager = () => {
    const manager = React.useContext(DocumentManagerContext);
    if (manager === undefined) throw new Error();
    const { addAttachment, getFileData, markAsRead, resetDocuments } = manager;
    return { addAttachment, getFileData, markAsRead, resetDocuments };
};